/**
 * Warenkorb-Rechnung der Kasse – alles in Cent (Regel 5). Die Summen je
 * Steuersatz dienen nur der Anzeige; verbindlich rechnet der Kern über die
 * API (summiereNachSteuersatz) aus denselben Positionen.
 */
export type Steuersatz = '20' | '10' | '13' | '0' | '19' | '4,9';

export const STEUERSAETZE: readonly { satz: Steuersatz; text: string }[] = [
  { satz: '20', text: '20 % Normal' },
  { satz: '10', text: '10 % Ermäßigt' },
  { satz: '13', text: '13 % Ermäßigt' },
  { satz: '0', text: '0 % Null' },
  { satz: '19', text: '19 % Jungholz/Mittelberg' },
  { satz: '4,9', text: '4,9 % Besonders' },
];

export interface Position {
  bezeichnung: string;
  menge: number;
  /** Brutto je Position (Menge × Einzelpreis) in Cent. */
  bruttoCent: number;
  steuersatz: Steuersatz;
  /** Artikel-ID aus dem Artikelstamm (Lagerabgang); frei erfasste Positionen ohne. */
  artikelId?: number;
  /**
   * Prozentrabatt auf diese Zeile (nur auf dem Gerät, der Server ignoriert das Feld):
   * Einzelpreis vor dem Nachlass und Prozentsatz. Mengenänderungen rechnen damit
   * genauso wie beim Setzen des Rabatts – kein Cent wandert durch Rundung.
   */
  zeilenNachlass?: { basisCent: number; prozent: number };
  /**
   * Exakter Einzelpreis aus dem Artikelstamm (Cent je Einheit). Mengenänderungen rechnen
   * damit statt aus dem gerundeten Zeilenbetrag – wie der Server: round(Einzelpreis × Menge).
   */
  einzelCent?: number;
  /** P8/S-5: Änderungscursor des Artikels, mit dem der Preis übernommen wurde (Server prüft). */
  artikelStand?: number;
  /** P8/S-5: Preis an der Kasse bewusst geändert (Rabattrecht wie bisher). */
  preisBewusst?: boolean;
}

export function summeBrutto(positionen: readonly Position[]): number {
  return positionen.reduce((s, p) => s + p.bruttoCent, 0);
}

export function summenNachSteuersatz(positionen: readonly Position[]): Map<Steuersatz, number> {
  const summen = new Map<Steuersatz, number>();
  for (const p of positionen) {
    summen.set(p.steuersatz, (summen.get(p.steuersatz) ?? 0) + p.bruttoCent);
  }
  return summen;
}

/** Netto und USt aus einem Bruttobetrag; Rundung kaufmännisch auf Cent (wie im Kern). */
export function nettoUndUst(
  bruttoCent: number,
  steuersatz: Steuersatz,
): { nettoCent: number; ustCent: number } {
  // Promille wie im Kern (ganzzahlig, 4,9 % = 49 ‰); Vorzeichen nach dem Runden.
  const promille = Math.round(Number(steuersatz.replace(',', '.')) * 10);
  const betrag = Math.round((Math.abs(bruttoCent) * 1000) / (1000 + promille));
  const nettoCent = bruttoCent < 0 ? 0 - betrag : betrag;
  return { nettoCent, ustCent: bruttoCent - nettoCent };
}

/** 123456 → "1.234,56" */
export function formatiereEuro(cent: number): string {
  const vorzeichen = cent < 0 ? '-' : '';
  const betrag = Math.abs(cent);
  const euro = Math.floor(betrag / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${vorzeichen}${euro},${String(betrag % 100).padStart(2, '0')}`;
}

/** "12,50" | "12.5" | "1.234,56" → Cent; null bei Unfug. */
export function parseEuro(text: string): number | null {
  const t = text.trim().replace(/\s|€/g, '');
  if (t === '') {
    return null;
  }
  // Deutsch: Punkt als Tausender, Komma als Dezimaltrennzeichen; sonst Punkt dezimal.
  const normalisiert = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalisiert)) {
    return null;
  }
  const [ganz = '0', dez = ''] = normalisiert.replace('-', '').split('.');
  const cent = Number(ganz) * 100 + Number(dez.padEnd(2, '0'));
  return normalisiert.startsWith('-') ? -cent : cent;
}

/** Positionen für einen Stornobeleg: Mengen bleiben, Beträge werden negiert. */
export function stornoPositionen(positionen: readonly Position[]): Position[] {
  return positionen.map((p) => {
    // Stammpreis gilt für die negierte Zeile nicht (einzelpreis() rechnet aus dem Betrag)
    const kopie: Position = { ...p, bruttoCent: -p.bruttoCent };
    delete kopie.einzelCent;
    return kopie;
  });
}
